import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  getProviderDashboardPipeline,
  getProviderDashboardSummary,
  getProviderReviewQueue,
} from '../../api/providerApi';
import { KpiCard } from '../../components/ui/KpiCard';
import { DataTable } from '../../components/ui/DataTable';
import { FilterTabs } from '../../components/ui/FilterTabs';
import { Pagination } from '../../components/ui/Pagination';
import { StatusPill } from '../../components/ui/StatusPill';
import { Button } from '../../components/ui/Button';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { IconPackage, IconShield, IconVault } from '../../components/ui/icons';
import { formatDateTime } from '../../utils/format';
import {
  authorityStateLabel,
  candidateDetailPath,
  nextHandlerLabel,
  recommendationLabel,
  taskStatusLabel,
} from './providerReviewLabels';

const HANDOFF_TABS = [
  { value: '', label: 'All candidates' },
  { value: 'ROUTED', label: 'Routed to Custodian' },
];

export function CustodyPackagingPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadHandoff() {
      setLoading(true);
      setError('');
      try {
        const [queueResponse, summaryResponse, pipelineResponse] = await Promise.all([
          getProviderReviewQueue({
            page,
            pageSize,
            recommendation: 'CANDIDATE',
            status: statusFilter || undefined,
          }),
          getProviderDashboardSummary().catch(() => null),
          getProviderDashboardPipeline().catch(() => null),
        ]);
        if (cancelled) return;
        setItems(queueResponse.items || []);
        setTotal(queueResponse.meta?.total || 0);
        setSummary(summaryResponse);
        setPipeline(pipelineResponse?.items || []);
      } catch (requestError) {
        if (cancelled) return;
        setItems([]);
        setTotal(0);
        setSummary(null);
        setPipeline([]);
        setError(
          requestError.message || 'Unable to load custody packaging data.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadHandoff();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, statusFilter, reloadToken]);

  const maxPipeline = Math.max(1, ...pipeline.map((stage) => stage.count || 0));

  const columns = useMemo(
    () => [
      {
        key: 'batch',
        header: 'Package',
        sortValue: (row) => row.batch,
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {row.batch || '—'}
            </span>
            <span className="block text-[11px] text-eorig-muted">
              {formatDateTime(row.created_at)}
            </span>
          </div>
        ),
      },
      {
        key: 'asset_number',
        header: 'Asset',
        render: (row) => row.asset_number || '—',
      },
      {
        key: 'program',
        header: 'Program',
        render: (row) =>
          row.program ? (
            <span className="inline-flex max-w-[160px] truncate rounded-md bg-eorig-surface px-2 py-1 text-[11px] text-eorig-muted">
              {row.program}
            </span>
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'recommendation',
        header: 'Recommendation',
        render: (row) =>
          row.recommendation ? (
            <StatusPill
              value={row.recommendation}
              label={recommendationLabel(row.recommendation)}
            />
          ) : (
            <span className="text-eorig-muted">None</span>
          ),
      },
      {
        key: 'task_status',
        header: 'Task status',
        render: (row) => (
          <StatusPill value={row.task_status} label={taskStatusLabel(row.task_status)} />
        ),
      },
      {
        key: 'authority_state',
        header: 'Authority',
        render: (row) => (
          <StatusPill
            value={row.authority_state}
            label={authorityStateLabel(row.authority_state)}
          />
        ),
      },
      {
        key: 'next',
        header: 'Handoff',
        sortValue: (row) => nextHandlerLabel(row),
        render: (row) => (
          <span className="text-eorig-ink">{nextHandlerLabel(row)}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-[13px] text-eorig-muted">
          Candidate packages for Custodian handoff. Routing stays on Authority
          Review.
        </p>
        <Link to="/provider/authority-review" className="shrink-0">
          <Button variant="gradient">Authority review</Button>
        </Link>
      </div>

      {error ? (
        <ErrorBanner
          message={error}
          onRetry={() => setReloadToken((value) => value + 1)}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
        <KpiCard
          label="Candidates"
          value={total}
          subline="Matching the current handoff filter"
          icon={IconPackage}
          accent
          loading={loading}
        />
        <KpiCard
          label="Awaiting Custodian"
          value={summary?.awaiting_custodian ?? 0}
          subline="Routed candidates pending legal authority"
          icon={IconShield}
          loading={loading}
        />
        <KpiCard
          label="Admitted"
          value={summary?.admitted ?? 0}
          subline="Custody accepted by Custodian"
          icon={IconVault}
          loading={loading}
        />
      </div>

      {pipeline.length ? (
        <section className="rounded-2xl border border-eorig-border bg-white p-5 shadow-eorig-card">
          <h2 className="text-sm font-semibold text-eorig-ink">Pipeline by stage</h2>
          <p className="mt-1 text-[12px] text-eorig-muted">
            Intake through custodian handoff from the Provider dashboard API.
          </p>
          <ul className="mt-5 space-y-4">
            {pipeline.map((stage) => {
              const pct = Math.round(((stage.count || 0) / maxPipeline) * 100);
              return (
                <li key={stage.stage} className="flex items-center gap-4">
                  <span className="w-36 shrink-0 text-sm text-eorig-muted">
                    {stage.label}
                  </span>
                  <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-full bg-eorig-surface">
                    <div
                      className="h-full rounded-full bg-eorig-gradient"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="w-10 shrink-0 text-right text-sm font-semibold tabular-nums text-eorig-ink">
                    {stage.count ?? 0}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <section className="space-y-3 rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        <FilterTabs
          tabs={HANDOFF_TABS}
          value={statusFilter}
          ariaLabel="Filter by handoff status"
          onChange={(value) => {
            setPage(1);
            setStatusFilter(value);
          }}
        />
      </section>

      <DataTable
        columns={columns}
        rows={items}
        rowKey={(row) => row.task_id}
        rowHref={(row) => candidateDetailPath(row.asset_id, row.task_id)}
        loading={loading}
        emptyMessage="No candidate packages for custodian handoff."
        minWidth={880}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        disabled={loading}
      />
    </div>
  );
}
